import React, { useEffect, useState } from 'react';
import I18n from 'i18n-js';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounceValue } from 'usehooks-ts';

import AsyncAssignmentModal from '@/components/staffer/AssignmentModal/AsyncAssignmentModal';
import { compareObjects, FILTER_FETCH_DEBOUNCE_MS, ignoreCancel } from '../common/utils';
import { userInfo } from '../actions';
import { Program, Lesson } from '../types';

import FilterBar from './FilterBar';
import GameCard from './GameCard';
import { getFilteredGames, getFilters, initialFilters } from './actions';
import styles from './index.module.scss';
import { GameFilter, GameInfo, Unit } from './types';

interface Props {
  program: Program;
  lesson?: Lesson;
  gradeUri: string;
  unit: Unit;
}

interface GamesGridProps {
  games: Array<GameInfo>;
  isLoading: boolean;
  onAssignClick: (
    gameId: number,
    lessonId: number,
    initialValues: { modelType: string; modelsToAssign: string }
  ) => void;
  onGradeClick: (lessonId: number) => void;
}

const GamesGrid = ({ games, isLoading, onAssignClick, onGradeClick }: GamesGridProps) => {
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <FontAwesomeIcon spin icon={faSpinner} />
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className={styles.noResult}>
        <div className={styles.noResultTitle}>{I18n.t('libraries.games.no_games_found')}</div>
        <div className={styles.noResultDescription}>
          {I18n.t('please_modify_filters')}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cardContainer}>
      {games.map(gameData => (
        <GameCard
          key={gameData.id}
          data={gameData}
          onAssignClick={onAssignClick}
          onGradeClick={onGradeClick}
        />
      ))}
    </div>
  );
};

const GameLibrary = ({ program, lesson, unit, gradeUri }: Props) => {
  const isStudent = !userInfo.staffer;
  const [isLoading, setIsLoading] = useState(true);

  const [games, setGames] = useState<Array<GameInfo>>([]);
  const [filters, setFilters] = useState<GameFilter>({ ...initialFilters });
  const [debouncedFilters] = useDebounceValue(filters, FILTER_FETCH_DEBOUNCE_MS);
  const [assignModalSetting, setAssignModalSetting] = useState({
    gameId: 0,
    initialValues: { modelType: 'lessonGame', modelsToAssign: '' },
    lessonId: 0,
    show: false,
  });

  useEffect(() => {
    getFilters(program.id, lesson?.id, unit?.id).then((res) => {
      setFilters({ ...res, category: res.category.filter(category => category.value !== 'classroom_game') });
    });
  }, [program.id]);

  useEffect(() => {
    if (!compareObjects(debouncedFilters, initialFilters) && !lesson) {
      localStorage.setItem('game-filters', JSON.stringify(debouncedFilters));
    }

    setIsLoading(true);
    getFilteredGames(debouncedFilters, program.id).then((res) => {
      setGames([...res].sort((infoA, infoB) => {
        const unitA = infoA.unit_title?.split(' ')[0] || '';
        const unitB = infoB.unit_title?.split(' ')[0] || '';
        if (unitA !== unitB) return unitA < unitB ? -1 : 1;
        if (infoA.lesson_id !== infoB.lesson_id) return infoA.lesson_id - infoB.lesson_id;
        return infoA.game_type === 'Vocabulary Game' ? 1 : -1;
      }));
      setIsLoading(false);
    }).catch(ignoreCancel);
  }, [debouncedFilters, program.id]);

  const handleFilterChange = (newFilters: GameFilter) => {
    setFilters(newFilters);
  };

  const handleAssignClick = (
    gameId: number,
    lessonId: number,
    initialValues: { modelType: string; modelsToAssign: string },
  ) => {
    setAssignModalSetting({ gameId, initialValues, lessonId, show: true });
  };

  const handlePostSave = () => {
    setGames(prevGames => prevGames.map(game => (
      game.id === assignModalSetting.gameId ? { ...game, assigned: true } : game
    )));
  };

  const handleGradeClick = (lessonId: number) => {
    const uri = new URL(`${window.location.protocol}//${window.location.host}/${gradeUri}`);
    if (lessonId) {
      uri.searchParams.set('lesson_id', lessonId.toString());
    }
    uri.searchParams.set('program_id', program.id);
    // eslint-disable-next-line no-restricted-globals
    location.href = uri.toString();
  };

  const handleCloseAssignModal = () => {
    setAssignModalSetting({
      gameId: 0,
      initialValues: { modelType: 'lessonGame', modelsToAssign: '' },
      lessonId: 0,
      show: false,
    });
  };

  return (
    <div className={styles.alignLeft}>
      <div className={`${styles.description} body`}>
        {isStudent
          ? I18n.t('libraries.games.description_student')
          : I18n.t('libraries.games.description_staffer')}
      </div>

      <AsyncAssignmentModal
        closeModal={handleCloseAssignModal}
        handlePostSave={handlePostSave}
        initialLessonId={assignModalSetting.lessonId}
        initialValues={assignModalSetting.initialValues}
        modalIsOpen={assignModalSetting.show}
        program={{ ...program, id: Number.parseInt(program.id, 10) }}
        stafferId={userInfo?.staffer?.id}
      />

      <div className={styles.container}>
        <FilterBar filters={filters} onFilterChange={handleFilterChange} />

        <GamesGrid
          games={games}
          isLoading={isLoading}
          onAssignClick={handleAssignClick}
          onGradeClick={handleGradeClick}
        />
      </div>
    </div>
  );
};

export default GameLibrary;
